import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import { CheckCircle, Circle, MapPin } from "@phosphor-icons/react";
import { TID } from "@/lib/testIds";

const FLOW = [
  { key: "paid", label: "Payment confirmed" },
  { key: "preparing", label: "We're picking your items" },
  { key: "ready_for_pickup", label: "Ready for our driver" },
  { key: "assigned", label: "Driver on the way to us" },
  { key: "picked_up", label: "Driver has your order" },
  { key: "delivering", label: "On the way to you" },
  { key: "delivered", label: "Delivered" },
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (mounted) setData(data);
      } catch (_) {
        // ignore transient
      }
    };
    load();
    const iv = setInterval(load, 4000);
    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, [orderId]);

  if (!data) return <div className="text-[var(--text-2)]">Loading…</div>;
  const { order, store } = data;
  const statusIdx = FLOW.findIndex((f) => f.key === order.status);
  const activeIdx = order.status === "cancelled" ? -1 : statusIdx;

  return (
    <div data-testid={TID.orderTracking} className="space-y-8">
      <div>
        <span className="label-eyebrow">Live order · #{order.order_id.slice(-6).toUpperCase()}</span>
        <h1 className="mt-1 font-display text-5xl sm:text-6xl leading-[0.95]">
          {order.status === "delivered"
            ? "Delivered · Geniet dit!"
            : order.eta_minutes
            ? `Arriving in ~${order.eta_minutes} min`
            : "Order in progress"}
        </h1>
        <p className="mt-2 text-[var(--text-2)]">From {store.name}</p>
      </div>

      <LiveMap
        store={store}
        delivery={{ lat: order.delivery_lat, lng: order.delivery_lng }}
        driver={order.driver_lat && order.driver_lng ? { lat: order.driver_lat, lng: order.driver_lng } : null}
        height={380}
        testId={TID.orderTrackingMap}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-panel p-6">
          <span className="label-eyebrow">Progress</span>
          <ol className="mt-4 space-y-3">
            {FLOW.map((step, i) => {
              const done = i <= activeIdx;
              const current = i === activeIdx;
              return (
                <li key={step.key} className="flex items-center gap-3">
                  {done ? (
                    <CheckCircle size={22} weight="fill" className={current ? "text-[var(--yellow-dark)]" : "text-[var(--green)]"} />
                  ) : (
                    <Circle size={22} weight="regular" className="text-[var(--text)]/25" />
                  )}
                  <span className={done ? "text-[var(--text)] font-bold" : "text-[var(--text-3)]"}>{step.label}</span>
                </li>
              );
            })}
          </ol>
        </div>
        <div className="card-panel p-6">
          <span className="label-eyebrow">Delivery</span>
          <div className="mt-3 flex items-start gap-2">
            <MapPin size={16} weight="fill" className="text-[var(--red)] mt-1" />
            <p className="text-sm text-[var(--text-2)]">{order.delivery_address}</p>
          </div>
          <div className="mt-6 pt-4 border-t-2 border-[var(--text)]/10 space-y-2 text-sm">
            <div className="flex justify-between text-[var(--text-2)]"><span>Items</span><span>{order.items.reduce((n, it) => n + it.quantity, 0)}</span></div>
            <div className="flex justify-between text-[var(--text-2)]"><span>Subtotal</span><span>R{order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-[var(--text-2)]"><span>Delivery</span><span>R{order.delivery_fee.toFixed(2)}</span></div>
            <div className="flex justify-between text-[var(--text-2)]"><span>VAT</span><span>R{order.tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-display text-2xl mt-3 pt-3 border-t-2 border-[var(--text)]"><span>Total</span><span>R{order.total.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
